import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonButton,
  IonText,
} from '@ionic/angular/standalone';
import { TranslateModule } from '@ngx-translate/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { IReleaseNote } from '../../types/release-note.interface';
import { ReleaseNotesService } from '../../services/release-notes.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-release-notes-modal',
  templateUrl: './release-notes-modal.component.html',
  styleUrls: ['./release-notes-modal.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    IonModal,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonContent,
    IonButtons,
    IonButton,
    IonText,
    TranslateModule,
    FontAwesomeModule,
  ],
})
export class ReleaseNotesModalComponent implements OnChanges {
  private releaseNotesService = inject(ReleaseNotesService);
  private cdr = inject(ChangeDetectorRef);
  @Input() modalTrigger: boolean = false;
  @Input() filterVersion: string | null = null;
  @Input() showLatestOnly: boolean = false;
  /**
   * Prefix for the data-test anchors of this instance.
   *
   * The component is rendered twice: globally from alert.component.html for
   * the update notice, and from about.component.html for the full history.
   * Both overlays stay in the DOM while closed — Ionic moves the element when
   * it presents it, and an `@if` around it leaves the moved node orphaned
   * instead of removing it. Distinct anchors are therefore the reliable way to
   * keep the two apart.
   */
  @Input() dataTestAttribute: string = 'release-notes-modal';
  @Output() closed = new EventEmitter<void>();
  @ViewChild(IonModal) modal!: IonModal;

  releaseNotes: IReleaseNote[] = [];
  isDevMode = !environment.production;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['modalTrigger'] && this.modalTrigger) {
      this.loadReleaseNotes();
    }
  }

  private loadReleaseNotes(): void {
    if (this.filterVersion) {
      this.releaseNotesService
        .getReleaseNoteByVersion(this.filterVersion)
        .subscribe((note) => {
          this.releaseNotes = note ? [note] : [];
          this.cdr.markForCheck();
        });
    } else if (this.showLatestOnly) {
      this.releaseNotesService.getReleaseNotes().subscribe((notes) => {
        this.releaseNotes = notes.length > 0 ? [notes[0]] : [];
        this.cdr.markForCheck();
      });
    } else {
      this.releaseNotesService.getReleaseNotes().subscribe((notes) => {
        this.releaseNotes = notes;
        this.cdr.markForCheck();
      });
    }
  }

  close(): void {
    this.modal.dismiss();
  }

  /**
   * Drops the trigger once the overlay is really gone.
   *
   * It runs on `didDismiss` rather than `willDismiss` so the parent only
   * learns about the close when the animation has finished. Both the local
   * field and the parent flag have to fall, otherwise the next click is a
   * no-op and the notes cannot be opened a second time.
   */
  onDidDismiss(): void {
    this.modalTrigger = false;
    this.cdr.markForCheck();
    this.closed.emit();
  }

  /**
   * Features and improvements as one list, features first.
   *
   * The modal used to print them as two blocks. Whether a change reads as
   * "new" or as "better" is a judgement call that nobody acts on, so the split
   * only cost the reader a second heading. Bug fixes keep their own block.
   */
  featuresAndImprovements(changes: IReleaseNote['changes']): string[] {
    return [...(changes.newFeatures ?? []), ...(changes.improvements ?? [])];
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }
}
